import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Star, Briefcase, GraduationCap, Crown, Heart, Sparkles, Building, Award, Check } from "lucide-react";
import { MAGIC_PHOTOS } from "../data/cruiseData";
import SEOHead from "../components/SEOHead";

const NETWORKING_PAGES = [
 { slug: "business-networking-cruises-sydney", title: "Business Networking Cruises", sub: "Industry mixers · 60 to 120 attendees", Icon: Briefcase, accent: "#E63E5E" },
 { slug: "womens-networking-events-sydney-harbour", title: "Women's Networking Events", sub: "Champagne · panels · keynotes", Icon: Heart, accent: "#FF4B8B" },
 { slug: "corporate-networking-cruises-sydney", title: "Corporate Networking Cruises", sub: "Sponsor-friendly format", Icon: Building, accent: "#FF8856" },
 { slug: "entrepreneur-networking-events-sydney", title: "Entrepreneur Networking Events", sub: "Founders · investors · BYO format", Icon: Sparkles, accent: "#FFC850" },
 { slug: "executive-networking-cruises-sydney", title: "Executive Networking Cruises", sub: "C-suite · board · senior partners", Icon: Crown, accent: "#3FA3FF" },
 { slug: "professional-networking-cruises-sydney", title: "Professional Networking Cruises", sub: "Law · finance · medical · agency", Icon: Award, accent: "#A24FE0" },
 { slug: "business-events-sydney-harbour", title: "Business Events Sydney Harbour", sub: "Conferences · galas · launches", Icon: Briefcase, accent: "#E63E5E" },
 { slug: "women-in-business-cruises-sydney", title: "Women In Business Cruises", sub: "Champagne · keynote · mentor rounds", Icon: Heart, accent: "#FF4B8B" },
 { slug: "leadership-event-cruises-sydney", title: "Leadership Event Cruises", sub: "ELT offsites · succession cohorts", Icon: Crown, accent: "#FF8856" },
 { slug: "startup-networking-cruises-sydney", title: "Startup Networking Cruises", sub: "Tech · fintech · climate · AI founders", Icon: GraduationCap, accent: "#FFC850" }];

export default function NetworkingEventsSydney() {
 React.useEffect(() => {
  const io = new IntersectionObserver((entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }), { threshold: 0.12 });
  document.querySelectorAll(".reveal-up").forEach(el => io.observe(el));
  return () => io.disconnect();
 }, []);

 return (
  <main className="bg-[#FFF7EE] text-[#0F1B3D] overflow-hidden" data-testid="networking-hub">
   <SEOHead
    title="Networking Events Sydney Harbour · Business, Women's, Executive & Startup Cruises | Magic Cruises"
    description="Sydney's premier private networking cruise venue. Business, corporate, women's, executive, entrepreneur, leadership and startup networking events on Sydney Harbour for 60 to 120 attendees. Premium catering, licensed bar, AV-ready, sponsor support."
    image={MAGIC_PHOTOS.magicSaloonNight}
    type="EventVenue"
   />

   {/* HERO */}
   <section className="relative pt-32 lg:pt-36 pb-20 lg:pb-28 overflow-hidden">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.magicSaloonNight} alt="Sydney Harbour networking event onboard Magic Cruises with champagne and city skyline backdrop" className="w-full h-full object-cover scale-105" />
     <div className="absolute inset-0" style={{ background: "linear-gradient(165deg, rgba(15,27,61,0.62) 0%, rgba(230,62,94,0.32) 50%, rgba(15,27,61,0.85) 100%)" }} />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 text-white reveal-up">
     <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 backdrop-blur-md" style={{ backgroundColor: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.28)" }}>
      <Users className="w-3.5 h-3.5" style={{ color: "#FFC850" }} />
      <span className="text-[11px] uppercase tracking-[0.3em] font-black">Sydney's Networking Venue</span>
     </div>
     <h1 className="font-black tracking-tight leading-[0.92] max-w-5xl" style={{ fontSize: "clamp(2.25rem, 5.5vw, 5.5rem)", letterSpacing: "-0.025em", textShadow: "0 4px 40px rgba(0,0,0,0.5)" }}>
      Networking Events<br /><span className="italic font-light" style={{ background: "linear-gradient(120deg, #FFC850 0%, #FF4B8B 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>on Sydney Harbour.</span>
     </h1>
     <p className="text-white/85 text-lg leading-[1.65] max-w-2xl mt-7 font-light">
      Sydney's most-RSVP'd business networking venue. Magic Cruises hosts private networking events on Sydney Harbour for 60 to 120 attendees, business mixers, women's networking, executive dinners, founder meetups, leadership offsites and industry galas. 90%+ attendance rate from confirmed RSVPs. Sydney's premium harbour view, an onboard chef, a fully licensed bar and a sponsor-friendly two-deck layout.
     </p>
     <div className="mt-9 flex flex-wrap gap-3">
      <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-black text-sm tracking-wide text-white shadow-xl" style={{ background: "linear-gradient(120deg, #E63E5E 0%, #FF4B8B 100%)" }}>
       Plan your event <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to="/packages" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm tracking-wide border-2 text-white" style={{ borderColor: "rgba(255,255,255,0.7)" }}>
       See pricing
      </Link>
     </div>
    </div>
   </section>

   {/* STAT STRIP */}
   <section className="relative bg-[#0F1B3D] text-white py-14 lg:py-16">
    <div className="max-w-7xl mx-auto px-5 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-8 lg:divide-x lg:divide-white/10">
     {[
      { num: "120", label: "Max attendees" },
      { num: "90%+", label: "Confirmed RSVP rate" },
      { num: "3hr", label: "Most-booked format" },
      { num: "10", label: "Networking event types" }].map((s, i) => (
      <div key={i} className="text-center px-4">
       <div className="font-black text-amber-300 tracking-tight leading-none" style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", letterSpacing: "-0.03em" }}>{s.num}</div>
       <div className="mt-3 text-white/70 text-[11px] uppercase tracking-[0.28em] font-bold">{s.label}</div>
      </div>
     ))}
    </div>
   </section>

   {/* WHY THE HARBOUR FOR NETWORKING */}
   <section className="relative py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-16 reveal-up">
      <div className="lg:col-span-5">
       <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-4 flex items-center gap-3" style={{ color: "#E63E5E" }}>
        <span className="w-8 h-px" style={{ backgroundColor: "#E63E5E" }} /> Why the harbour wins
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.022em" }}>
        Networking Sydney<br /><span className="italic font-light" style={{ color: "#E63E5E" }}>actually shows up to.</span>
       </h2>
      </div>
      <div className="lg:col-span-7">
       <p className="text-lg leading-[1.7] font-light" style={{ color: "rgba(15,27,61,0.72)" }}>
        Sydney's professional networking calendar is brutal, every chamber, association and agency runs functions, and most attendees skip 40% of what they say yes to. A private cruise on Sydney Harbour changes the maths: confirmed-attendance rates jump above 90%, post-event recall triples, sponsor visibility multiplies, and the photos circulate for weeks afterwards. The harbour does work no land-based venue can replicate.
       </p>
       <ul className="mt-7 grid sm:grid-cols-2 gap-3">
        {[
         "Capacity 60 to 120 standing · 95 cocktail service", "Three-hour format (board 6pm · return 9:30pm)", "Premium canapé service from $45, $65 per head", "Beverage package $15.50 per head / hour", "AV-ready for host segments, keynotes & sponsors", "Two-deck layout breaks crowd into mood zones", "Sponsor banner & branding support throughout", "King St Wharf boarding · central Sydney CBD"].map((b, i) => (
         <li key={i} className="flex gap-2.5 items-start text-sm font-light leading-snug" style={{ color: "rgba(15,27,61,0.72)" }}>
          <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#E63E5E" }} />{b}
         </li>
        ))}
       </ul>
      </div>
     </div>
    </div>
   </section>

   {/* 10 NETWORKING PAGES GRID */}
   <section className="relative py-20 lg:py-28 bg-[#FFE9E2]">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
     <div className="max-w-3xl mb-12 reveal-up">
      <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-4 flex items-center gap-3" style={{ color: "#FF4B8B" }}>
       <span className="w-8 h-px" style={{ backgroundColor: "#FF4B8B" }} /> Browse by event type
      </div>
      <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.022em" }}>
       Ten networking formats.<br /><span className="italic font-light" style={{ color: "#FF4B8B" }}>One harbour.</span>
      </h2>
     </div>

     <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 reveal-up">
      {NETWORKING_PAGES.map((p, i) => (
       <Link
        key={i}
        to={`/cruises/${p.slug}`}
        className="group relative rounded-3xl bg-white p-6 lg:p-7 overflow-hidden hover:-translate-y-1.5 transition-all duration-500 shadow-[0_15px_40px_-18px_rgba(15,27,61,0.18)] hover:shadow-[0_25px_60px_-20px_rgba(15,27,61,0.4)]"
        data-testid={`networking-card-${p.slug}`}
       >
        <div className="absolute top-0 left-0 right-0 h-1.5" style={{ backgroundColor: p.accent }} />
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-25 group-hover:opacity-50 transition-opacity" style={{ background: p.accent }} />
        <div className="relative">
         <div className="w-12 h-12 rounded-2xl inline-flex items-center justify-center shadow-md mb-5 ring-2 ring-white/20" style={{ backgroundColor: p.accent }}>
          <p.Icon className="w-6 h-6 text-white" strokeWidth={2.4} />
         </div>
         <div className="font-black text-base leading-tight tracking-tight">{p.title}</div>
         <div className="text-[11px] uppercase tracking-[0.18em] font-bold mt-2.5" style={{ color: "rgba(15,27,61,0.6)" }}>{p.sub}</div>
         <div className="mt-5 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.28em] font-black group-hover:gap-2.5 transition-all" style={{ color: p.accent }}>
          Explore <ArrowRight className="w-3 h-3" />
         </div>
        </div>
       </Link>
      ))}
     </div>
    </div>
   </section>

   {/* FORMATS WE RUN */}
   <section className="relative py-20 lg:py-28">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
     <div className="max-w-3xl mb-12 reveal-up">
      <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-4 flex items-center gap-3" style={{ color: "#FF8856" }}>
       <span className="w-8 h-px" style={{ backgroundColor: "#FF8856" }} /> Networking formats we run
      </div>
      <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.022em" }}>
       Five formats.<br /><span className="italic font-light" style={{ color: "#FF8856" }}>Built to convert RSVPs.</span>
      </h2>
     </div>
     <div className="grid lg:grid-cols-2 gap-5 lg:gap-6 reveal-up">
      {[
       { title: "Champagne arrival + canapé mixer", body: "3-hour Sunday-style mingle. Champagne on arrival, Canapé Menu B, freeflow conversation. The default women's-networking and chamber-mixer format." },
       { title: "Keynote + Q&A networking", body: "30-minute address from a senior leader via AV, followed by 2 hours of freeflow mingling with full canapé and bar service. Ideal for industry-body events." },
       { title: "Panel + sponsor activation", body: "4 panellists, moderated discussion, audience Q&A, then 90 minutes of networking with branded sponsor activations on both decks." },
       { title: "Executive dinner cruise", body: "Seated Diamond Buffet for up to 40 senior executives. Premium spirits bar, white-glove service, optional 20-minute strategy briefing over AV." },
       { title: "Founder-investor mixer (BYO)", body: "Budget-friendly BYO charter format for startup ecosystems. Zero corkage. Sunset boarding for golden-hour content. Up to 50 founders + investors." },
       { title: "Industry gala / annual function", body: "4-hour evening charter, Emerald or Diamond Buffet, beverage package, sponsor branding throughout, optional awards segment, photographer." }].map((f, i) => (
       <div key={i} className="rounded-3xl bg-white p-7 lg:p-8 shadow-[0_15px_40px_-18px_rgba(15,27,61,0.18)]" data-testid={`format-${i}`}>
        <div className="font-black text-xl tracking-tight">{f.title}</div>
        <p className="mt-3 text-[15px] leading-[1.65] font-light" style={{ color: "rgba(15,27,61,0.72)" }}>{f.body}</p>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* FAQ */}
   <section className="relative py-20 lg:py-28 bg-[#FFE9E2]">
    <div className="max-w-5xl mx-auto px-5 lg:px-10">
     <div className="max-w-3xl mb-12 reveal-up">
      <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-4 flex items-center gap-3" style={{ color: "#E63E5E" }}>
       <span className="w-8 h-px" style={{ backgroundColor: "#E63E5E" }} /> FAQ
      </div>
      <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)", letterSpacing: "-0.022em" }}>
       Networking on the<br /><span className="italic font-light" style={{ color: "#E63E5E" }}>harbour, answered.</span>
      </h2>
     </div>
     <div className="space-y-3 reveal-up">
      {[
       { q: "What's the minimum group size for a networking cruise?", a: "60 attendees is typical for a mixer, but we've run focused executive dinners for 12. Magic Cruises charters are exclusive private use, you pay for the boat, not per head, so smaller groups are absolutely welcome." },
       { q: "Can sponsors brand the venue?", a: "Yes, banners on both decks, branded signage at boarding, branded cocktail menus, AV projector for sponsor segments, and a hidden photographer to capture sponsor-quality content for post-event marketing." },
       { q: "How does AV work onboard?", a: "We have an indoor saloon Bluetooth sound system with mic input and a projector for short keynote / sponsor segments. The lower deck doubles as a 40-seat theatre format if needed." },
       { q: "Do you handle dietary requirements?", a: "Vegetarian, vegan, gluten-free, halal, kosher and most allergy requirements handled by the onboard chef. Flag dietary needs at booking and we'll tailor the canapé selection." },
       { q: "Are networking cruises tax-deductible as business entertainment?", a: "Most corporate networking cruises qualify under standard ATO business entertainment rules, we'll provide an itemised tax invoice to your finance team after the event." },
       { q: "Can we have a cash bar instead of a beverage package?", a: "Yes, bar tab (pre-paid, min $500) or pure cash bar (guests pay-per-drink) both supported. Drink prices: beers $10, wine/sparkling $9, soft drinks $4, spirits $14." }].map((f, i) => (
       <details key={i} className="group rounded-2xl bg-white border border-blue-100 overflow-hidden">
        <summary className="px-6 py-5 cursor-pointer flex items-center justify-between gap-4 font-black list-none">
         <span className="text-base lg:text-lg tracking-tight">{f.q}</span>
         <span className="text-[#E63E5E] text-2xl transition-transform group-open:rotate-45">+</span>
        </summary>
        <div className="px-6 pb-6 text-sm lg:text-base font-light leading-[1.7]" style={{ color: "rgba(15,27,61,0.72)" }}>{f.a}</div>
       </details>
      ))}
     </div>
    </div>
   </section>

   {/* CTA */}
   <section className="relative py-24 lg:py-32 overflow-hidden text-white">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.magicSaloonNight} alt="" className="w-full h-full object-cover scale-105" />
     <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,27,61,0.95) 0%, rgba(230,62,94,0.78) 100%)" }} />
    </div>
    <div className="relative max-w-3xl mx-auto px-5 lg:px-10 text-center">
     <h2 className="font-black tracking-tight leading-[0.92]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", letterSpacing: "-0.025em" }}>
      Plan your<br /><span className="italic font-light" style={{ color: "#FFC850" }}>networking cruise.</span>
     </h2>
     <p className="text-white/85 text-lg lg:text-xl mt-6 font-light leading-[1.65]">
      One enquiry. One private boat. One unforgettable industry night on Sydney Harbour.
     </p>
     <div className="mt-9 flex flex-wrap justify-center gap-4">
      <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-sm tracking-wide text-white shadow-2xl" style={{ background: "linear-gradient(120deg, #E63E5E 0%, #FF4B8B 100%)" }}>
       Get a custom quote <ArrowRight className="w-4 h-4" />
      </Link>
      <a href="tel:1300100502" className="inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-sm tracking-wide border-2 text-white" style={{ borderColor: "rgba(255,255,255,0.7)" }}>
       Call 1300 100 502
      </a>
     </div>
    </div>
   </section>
  </main>
 );
}
